
import { createContext, ReactNode, useContext } from 'react';
import api from '../services/api';

interface Installment {
    id: string;
    subsidiary: string;
    supplier: string;
    installment_count: number;
    installment_amount: number;
    installment_number: string;
    notes: string;
    first_expiration_date: string;
    product: string;
    is_indirect_cost: boolean;
    payment_type: string;
    cost_category: string;
}

interface InstallmentFilter {
    subsidiary: string;
    supplier: string;
    status: string;
    startDate: string,
    endDate: string,
    page: number;
    limit: number;
}

interface InstallmentOrder {
    parcel_index: number;
    value_parcel: number;
    expiration_date: string;
    payment_date: string;
    status: string;
    notes: string;
}

type InstallmentInput = Omit<Installment, 'id'>

interface InstallmentsProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface ResponsePropsWithData {
    success: boolean;
    message: string;
    data: any;
}

interface InstallmentsContextData {
    getInstallments: (filter: InstallmentFilter) => Promise<ResponsePropsWithData>;
    getInstallment: (search: string) => Promise<ResponsePropsWithData>;
    getInstallmentOrders: (installmentId: string) => Promise<ResponsePropsWithData>;
    createInstallment: (installment: InstallmentInput) => Promise<ResponseProps>;
    updateInstallmentOrder: (orderId: string, installmentOrder: InstallmentOrder) => Promise<ResponseProps>;
    deleteInstallment: (installment_id: number) => Promise<ResponseProps>;
}

const InstallmentsContext = createContext<InstallmentsContextData>(
    {} as InstallmentsContextData
);

export function InstallmentsProvider({ children }: InstallmentsProvidersProps) {
  async function getInstallments(filter: InstallmentFilter) {
    let params = {
      ...filter
    };

    const response = await api.get('/installments', { params: { ...api.defaults.params, ...params } });

    if (response.status === 200) {
      return response.data;
    }
    return {
      success: false,
      message: "Erro ao consultar parcelamentos."
    };
  }

  async function getInstallment(search: string) {
    const response = await api.get(`/installments/${search}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar parcelamento.",
      data: []
    };
  }

  async function getInstallmentOrders(installmentId: string) {
    const response = await api.get(`/installments/${installmentId}/orders `);

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }
    return {
      success: false,
      message: "Erro ao consultar parcelas do parcelamento.",
      data: []
    };
  }

  async function deleteInstallment(installment_id: number) {
    const response = await api.delete(`/installments/${installment_id}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Parcelamento removido com sucesso!"
      };
    }

    return {
      success: false,
      message: "Erro ao remover o parcelamento."
    };
  }

  async function createInstallment(installment: InstallmentInput) {
    const response = await api.post('/installments', installment);

    if (response.status === 201) {
      return {
        success: true,
        message: "Parcelamento adicionado com sucesso!"
      };
    }

    return {
      success: false,
      message: "Erro ao adicionar parcelamento."
    };
  }

  async function updateInstallmentOrder(orderId: string, installmentOrder: InstallmentOrder) {
    const response = await api.put(`/installments/orders/${orderId}`, installmentOrder);

    if (response.status === 200) {
      return {
        success: true,
        message: "Parcela do parcelamento alterada com sucesso!"
      };
    }

    return {
      success: false,
      message: "Erro ao alterar a parcela do parcelamento."
    };
  }

  return (
    <InstallmentsContext.Provider value={{
      getInstallments,
      getInstallment,
      getInstallmentOrders,
      createInstallment,
      updateInstallmentOrder,
      deleteInstallment
    }}>
      {children}
    </InstallmentsContext.Provider>
  );
}

export function UseInstallments() {
  const context = useContext(InstallmentsContext);
  return context;
}
